/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import Card from '../components/Card';
import Form from '../components/Form';
import SelectWithSearch from '../components/SelectWithSearch';
import CreateRoleForm from '../components/CreateRoleForm';
import AddButton from '../components/AddButton';
import populator from '../utils/populator';
import UserValidationSchema from '../validationSchemas/userValidation';
import usePrevious from '../hooks/usePrevious';

import { StepFooter, UsersTable } from './commons';

import './modals.scss';

function NewUserManual({ onFinish, navigateBack }) {
  const roles = useSelector((state) => state.ecosystems.dictionaries.groups || []);
  const createdObject = useSelector((state) => state.objects.createdObject);
  const prevCreatedObject = usePrevious(createdObject);
  const [addedUsers, setAddedUsers] = useState([]);
  const [errors, setErrors] = useState([]);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState(null);

  useEffect(() => {
    if (createdObject !== prevCreatedObject && createdObject?.field === 'user') {
      setNewRole({
        value: createdObject.object.uuid,
        label: createdObject.object.name,
      });
    }
  }, [createdObject, prevCreatedObject]);

  const validate = (newUserData) => {
    try {
      UserValidationSchema.validateSync(newUserData, { abortEarly: false });
      setErrors([]);
      return true;
    } catch (err) {
      setErrors(err.errors);
      return false;
    }
  };

  const handleAddUser = () => {
    if (!newFirstName && !newLastName && !newEmail && !newPassword && !newRole) {
      return true;
    }

    if (!newRole) {
      setErrors(['Please select role']);
      return false;
    }

    const addUser = {
      firstName: newFirstName,
      lastName: newLastName,
      email: newEmail,
      password: newPassword,
      securityGroup: '',
      role: newRole,
      source: '',
      status: 'Pending',
    };

    if (!validate(addUser)) {
      return false;
    }

    setAddedUsers((prevAddedUsers) =>
      prevAddedUsers.some(
        (user) =>
          user.firstName === newFirstName &&
          user.lastName === newLastName &&
          user.email === newEmail &&
          user.role === newRole,
      )
        ? prevAddedUsers
        : [...prevAddedUsers, addUser],
    );
    setNewFirstName('');
    setNewLastName('');
    setNewEmail('');
    setNewRole(null);
    setNewPassword('');
    return true;
  };

  const handleSubmit = async () => {
    if (!(await handleAddUser())) {
      return;
    }

    try {
      onFinish({ type: 'user', data: addedUsers });
    } catch (err) {
      setErrors(['Failed to Add User']);
    }
  };

  const removeUser = (index) => {
    setAddedUsers((prevAddedUsers) => prevAddedUsers.filter((val, ind) => ind !== index));
  };

  return (
    <div>
      <div className="modal__content padded ldap-config new-user-survey-manual">
        {errors.map((err, index) => (
          <p className="error" key={index}>
            {err}
          </p>
        ))}
        <Card header={false}>
          <div className="form-row gateway-config">
            <Form.Group full>
              <UsersTable users={addedUsers} removeUser={removeUser}>
                <tr className="new-entry entries">
                  <td className="fName">
                    <Form.Text
                      value={newFirstName}
                      onChange={(value) => setNewFirstName(value)}
                      extraClass="light fName"
                      placeholder="First Name"
                    />
                  </td>
                  <td className="lName">
                    <Form.Text
                      value={newLastName}
                      onChange={(value) => setNewLastName(value)}
                      extraClass="light lName"
                      placeholder="Last Name"
                    />
                  </td>
                  <td className="role">
                    <Form.Group>
                      <SelectWithSearch
                        selected={newRole}
                        onChange={(value) => setNewRole(value)}
                        placeholder="Select role"
                        groupCreative
                        field="user"
                        createFormComponent={CreateRoleForm}
                        options={roles.map(populator.role)}
                      />
                    </Form.Group>
                  </td>
                  <td className="email">
                    <Form.Text
                      value={newEmail}
                      onChange={(value) => setNewEmail(value)}
                      extraClass="light email"
                      placeholder="Email"
                    />
                  </td>
                  <td className="pwd">
                    <Form.Text
                      type="password"
                      value={newPassword}
                      onChange={(value) => setNewPassword(value)}
                      extraClass="light email"
                      placeholder="Password"
                    />
                  </td>
                  <td className="last ">
                    <AddButton onClick={handleAddUser} />
                  </td>
                </tr>
              </UsersTable>
            </Form.Group>
          </div>
        </Card>
      </div>
      <div className="wedge-modal__footer">
        <StepFooter edit={false} submit onClick={handleSubmit} onBack={navigateBack} />
      </div>
    </div>
  );
}

NewUserManual.propTypes = {
  onFinish: PropTypes.func.isRequired,
  navigateBack: PropTypes.func,
};

NewUserManual.Footer = StepFooter;

export default NewUserManual;
